import { useState } from 'react';
import { Alert, Button, Group, Modal, Stack, Table, Text, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hasCapability } from '@prooffleet/contracts';
import { IconPlus } from '@tabler/icons-react';
import { ApiError, api, useSession } from '@prooffleet/client';
import { EmptyState, PageHeader, TableSkeleton } from '../components/Page.js';

interface SiteRow {
  _id: string;
  name: string;
  code: string;
  timezone: string | null;
  geofence: { lat: number; lon: number; radiusMeters: number } | null;
  unitCount?: number;
}

/**
 * Площадки — терминалы и дворы.
 *
 * От площадки зависят две вещи, которые не видны на этом экране, но ломаются
 * без неё: маршрутизация уведомлений (менеджер Далласа не должен получать
 * алерты Хьюстона) и область роли. Геозона участвует в оценке достоверности:
 * осмотр далеко от приписанного двора — сигнал, а не приговор.
 */
export function Sites(): React.JSX.Element {
  const actor = useSession((s) => s.actor);
  const canWrite = actor !== null && hasCapability(actor, 'site:write');
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<SiteRow | null>(null);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [timezone, setTimezone] = useState('America/Chicago');
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [radius, setRadius] = useState('300');
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['sites'],
    queryFn: () => api<{ sites: SiteRow[] }>('/sites'),
  });

  const sites = data?.sites ?? [];

  const reset = (): void => {
    setEditing(null);
    setName('');
    setCode('');
    setTimezone('America/Chicago');
    setLat('');
    setLon('');
    setRadius('300');
    setError(null);
  };

  const save = useMutation({
    mutationFn: async () => {
      const geofence =
        lat === '' || lon === ''
          ? null
          : { lat: Number(lat), lon: Number(lon), radiusMeters: Number(radius) };
      const body = { name, code, timezone, geofence };
      if (editing === null) return api('/sites', { method: 'POST', body });
      return api(`/sites/${editing._id}`, { method: 'PATCH', body });
    },
    onSuccess: async () => {
      setOpen(false);
      reset();
      await queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Could not save the site');
    },
  });

  return (
    <Stack gap={0}>
      <PageHeader
        title="Sites"
        count={isLoading ? undefined : sites.length}
        hint="Yards and terminals. Alert routing and role scope follow the site, so a manager in one yard is not paged about another."
        action={
          canWrite ? (
            <Button
              leftSection={<IconPlus size={15} stroke={1.8} />}
              onClick={() => {
                reset();
                setOpen(true);
              }}
            >
              Add site
            </Button>
          ) : undefined
        }
      />

      {isLoading && <TableSkeleton rows={3} cols={4} />}

      {!isLoading && sites.length === 0 && (
        <EmptyState
          title="No sites yet"
          hint="A fleet can run on a single site. Add one to group units and route alerts."
        />
      )}

      {!isLoading && sites.length > 0 && (
        <Table.ScrollContainer minWidth={680}>
          <Table className="pf-table" layout="fixed">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w="30%">Name</Table.Th>
                <Table.Th w="14%">Code</Table.Th>
                <Table.Th w="24%">Time zone</Table.Th>
                <Table.Th w="20%">Geofence</Table.Th>
                <Table.Th w="12%" />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sites.map((site) => (
                <Table.Tr key={site._id}>
                  <Table.Td>
                    <Text fw={580} fz="sm">
                      {site.name}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text className="pf-mono">{site.code}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fz="sm" c="gray.7">
                      {site.timezone ?? 'not set'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {site.geofence === null ? (
                      <Text fz="sm" c="dimmed">
                        not set
                      </Text>
                    ) : (
                      <Text className="pf-mono pf-num">
                        {site.geofence.lat.toFixed(3)}, {site.geofence.lon.toFixed(3)} ·{' '}
                        {site.geofence.radiusMeters} m
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td ta="right">
                    <Button
                      className="pf-row-action"
                      size="compact-xs"
                      variant="default"
                      disabled={!canWrite}
                      onClick={() => {
                        setEditing(site);
                        setName(site.name);
                        setCode(site.code);
                        setTimezone(site.timezone ?? 'America/Chicago');
                        setLat(site.geofence === null ? '' : String(site.geofence.lat));
                        setLon(site.geofence === null ? '' : String(site.geofence.lon));
                        setRadius(site.geofence === null ? '300' : String(site.geofence.radiusMeters));
                        setError(null);
                        setOpen(true);
                      }}
                    >
                      Edit
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      <Modal
        opened={open}
        onClose={() => setOpen(false)}
        title={editing === null ? 'New site' : `Edit ${editing.name}`}
      >
        <Stack gap="md">
          <TextInput
            label="Name"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            placeholder="Dallas Yard"
          />
          <TextInput
            label="Code"
            description="Short identifier, letters and digits"
            value={code}
            onChange={(e) => setCode(e.currentTarget.value.toUpperCase())}
            placeholder="DAL"
          />
          <TextInput
            label="Time zone"
            description="Schedules are written in wall time and converted using this zone"
            value={timezone}
            onChange={(e) => setTimezone(e.currentTarget.value)}
            placeholder="America/Chicago"
          />

          <div>
            <Text fw={500} fz="sm">
              Geofence
            </Text>
            <Text c="dimmed" fz="xs" mb={6}>
              Optional. Used as a trust signal: an inspection far from the yard is flagged, not
              rejected.
            </Text>
            <Group grow gap="xs">
              <TextInput
                placeholder="latitude"
                value={lat}
                onChange={(e) => setLat(e.currentTarget.value)}
              />
              <TextInput
                placeholder="longitude"
                value={lon}
                onChange={(e) => setLon(e.currentTarget.value)}
              />
              <TextInput
                placeholder="radius, m"
                value={radius}
                onChange={(e) => setRadius(e.currentTarget.value)}
              />
            </Group>
          </div>

          {error !== null && <Alert color="red">{error}</Alert>}

          <Button
            loading={save.isPending}
            disabled={name.trim() === '' || code.trim() === ''}
            onClick={() => save.mutate()}
          >
            {editing === null ? 'Create site' : 'Save'}
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
